import { ComponentType } from 'react';

import { Form } from './Form';
import { Submit } from './Submit';
import { fields } from './fields';
import { useField, UseFieldOptions } from './fields/useField';
import { NestedKeyOf } from './types';
import { useFormContext } from './useFormContext';

/**
 * A utility function for creating a typed form with enhanced fields.
 *
 * @template Value - The type of form values.
 * @returns {object} An object with properties for the Form, Submit component, Field components,
 * and the useFormContext hook, all properly typed.
 */
export const TypedForm = <Value extends object>() => ({
  Form: Form<Value>,
  Submit,
  Field: fields as TypedFormField<Value>,
  useFormContext: () => useFormContext<Value>(),
  useField: <N extends NestedKeyOf<Required<Value>>>(
    name: N,
    options: UseFieldOptions = {}
  ) => useField<ValueAtPath<Required<Value>, N>>(name, options),
});

type EnhancedFields<Fields, Keys> = {
  [K in keyof Fields]: Fields[K] extends ComponentType<infer Props>
    ? ComponentType<Props & { name: Keys }>
    : Fields[K];
};

export type TypedFormField<Value> = EnhancedFields<
  typeof fields,
  NestedKeyOf<Required<Value>>
>;

// Вспомогательный тип для получения типа по вложенному пути
type ValueAtPath<T, P extends string> = P extends `${infer K}.${infer Rest}`
  ? K extends keyof T
    ? ValueAtPath<T[K], Rest>
    : never
  : P extends keyof T
  ? T[P] | undefined
  : never;
